import React from 'react';
import { Link } from '@inertiajs/react';
import type { DevelopersPageProps, RestEndpoint } from '../../types/developers';
import CopyBlock from '../worklog/CopyBlock';
import Callout from './Callout';
import TokenStep from './TokenStep';
import { InlineText } from './inline';
import { fillPlaceholders, type ClientMeta } from './clients';
import { topicPath } from './topics';
import { inlineCode, ModeSwitch, pageTitle, StepHeading, type InstallMode } from './ui';

// The REST contract on the scripts page: what the curl in step 2 returns.
const RestReference: React.FC<{ endpoints: RestEndpoint[] }> = ({ endpoints }) => (
  <>
    <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
      <li>
        列表回 <code className={inlineCode}>{'{ data, meta }'}</code>，单条回 <code className={inlineCode}>{'{ data }'}</code>
        ，出错回 <code className={inlineCode}>{'{ error }'}</code>。<code className={inlineCode}>meta.total</code> 是分页前的总数。
      </li>
      <li>
        <code className={inlineCode}>:id</code> 两种都收：整数主键，或不可枚举的 <code className={inlineCode}>url_key</code>
        （12 位）。不想在日志里留下可枚举的 id 就用后者。
      </li>
      <li>
        写入参数是扁平的，不用嵌在 <code className={inlineCode}>task</code> 键下。
      </li>
    </ul>
    <table className="w-full text-xs">
      <tbody>
        {endpoints.map((e) => (
          <tr key={`${e.verb} ${e.path}`} className="border-t border-gray-200">
            <td className="w-16 py-1 pr-2 font-mono">{e.verb}</td>
            <td className="py-1 font-mono">{e.path}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

// One client per page: get a token → copy one thing → confirm (spec §5.2).
// Claude Desktop has its own page (DesktopTopic).
const ClientTopic: React.FC<{ client: ClientMeta; page: DevelopersPageProps }> = ({ client, page }) => {
  const token = page.new_token?.token;
  const prompt = page.install_prompts[client.kind];
  const [mode, setMode] = React.useState<InstallMode>(prompt ? 'ai' : 'manual');
  const urls = { mcpUrl: page.mcp_url, apiBase: page.api_base_url };

  return (
    <>
      <h1 className={pageTitle}>{client.label}</h1>
      <p className="mt-1.5 text-sm text-gray-700">{client.route}</p>

      <StepHeading n={1} title="拿一个 token" />
      <TokenStep topic={client.slug} clientLabel={client.label} newToken={page.new_token} />

      <StepHeading n={2} title={prompt ? '复制一段' : '调一次试试'} />
      <div className="space-y-2.5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {prompt && <ModeSwitch mode={mode} onChange={setMode} />}

        {mode === 'ai' && prompt ? (
          <>
            <p className="text-sm text-gray-700">
              把这段整个发给 {client.label}。{token ? '' : '它会先问你要 token。'}
            </p>
            <CopyBlock code={prompt} clampLines={6} />
          </>
        ) : (
          client.manual && (
            <>
              <p className="text-sm text-gray-700">{client.manual.intro}</p>
              <CopyBlock code={fillPlaceholders(client.manual.code, urls, token)} />
              {client.manual.note && (
                <p className="text-xs text-gray-500">{fillPlaceholders(client.manual.note, urls, token)}</p>
              )}
            </>
          )
        )}

        {client.kind === 'other' && <RestReference endpoints={page.rest_endpoints} />}
      </div>

      <StepHeading n={3} title="确认接上了" />
      {client.verify && (
        <p className="text-sm text-gray-700">
          <InlineText text={client.verify} />
        </p>
      )}
      {client.gotcha && (
        <div className="mt-2.5">
          <Callout tone="warn" title="撞墙了先看这个">
            {client.gotcha}
          </Callout>
        </div>
      )}

      {client.kind === 'claude_code' && (
        <p className="mt-5 text-sm text-gray-500">
          可选：
          <Link href={topicPath('worklog-hook')} className="text-blue-600">
            Worklog skills（每回合自动写工作日志）→
          </Link>
        </p>
      )}
    </>
  );
};

export default ClientTopic;
